// Dependencies
import { FC } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import { useMutation, useQueryClient } from "react-query";

// Components
import { FormErrorMessage, StyledCard } from "~/components/Common";
import UserRoleSelect from "~/components/Wallets/WalletAddMember/UserRoleSelect";
import OtherUserSelect from "~/components/Wallets/WalletAddMember/OtherUserSelect";

// api
import { shareWallet } from "~/services/wallet";

// Types
import { QUERY_KEY } from "~/enums";
import { TQueryError } from "~/types";

// Styles
import {
  StyledLabel,
  StyledTextInput,
  StyledShareButton,
} from "./AddMemberForm.styled";

interface AddMemberFormProps {
  walletId: string;
  members?: string[];
  onClose: Function;
}

type SharedWalletValues = {
  sharedUsername: string;
};

const AddMemberForm: FC<AddMemberFormProps> = ({
  walletId,
  members,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const sharedWalletMutation = useMutation({
    mutationFn: shareWallet,
    onSuccess() {
      enqueueSnackbar("Share Wallet success!", { variant: "success" });
      queryClient.invalidateQueries(QUERY_KEY.WALLET.WALLET_INFO);
      onClose();
    },
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  // methods
  const handleSubmitForm = (values: SharedWalletValues) => {
    console.log("share wallet");
    sharedWalletMutation.mutate({
      ...values,
      walletInforID: walletId,
    });
  };

  return (
    <StyledCard>
      <Formik
        initialValues={{
          sharedUsername: "",
        }}
        // validate form values
        validationSchema={Yup.object({
          sharedUsername: Yup.string().required("User is required"),
        })}
        // handle submit form
        onSubmit={handleSubmitForm}
      >
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <Form>
            <StyledTextInput
              name="sharedUsername"
              placeholder="Enter user"
              value={values.sharedUsername}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <FormErrorMessage
              error={errors.sharedUsername}
              hasError={!!(errors.sharedUsername && touched.sharedUsername)}
            />
            <StyledLabel>User role</StyledLabel>
            <UserRoleSelect />
            <StyledLabel>Other users</StyledLabel>
            {members?.map((member) => (
              <OtherUserSelect walletId={walletId} memberName={member} />
            ))}
            <StyledShareButton type={"submit"} variant={"contained"}>
              Share
            </StyledShareButton>
          </Form>
        )}
      </Formik>
    </StyledCard>
  );
};

export default AddMemberForm;
